/**
 * Shared goal-status vocabulary (issue #197).
 *
 * Follow-up from #193's sweep (PR #196): #193 fixed the Dashboard
 * *reading* the legacy `active` status; the value was still *writable* —
 * `CoachPortal.jsx`'s `GOAL_STATUS` offered it as a creatable option (and
 * `statusColor`/`humanStatus` mapped it), `api/src/db/seed.ts` wrote one,
 * and `api/src/routes/goals.ts` had no enum so nothing stopped drift.
 *
 * This is the single source of truth on the frontend side — CoachPortal.jsx
 * (coach-authored writes) and DevelopmentPlan.jsx (player reads/writes)
 * both import from here instead of each keeping its own list. The API has
 * the equivalent single source at `api/src/lib/goalStatus.ts`, consumed
 * by both `routes/goals.ts` (the zod enum) and `db/seed.ts`. The two
 * can't literally be one shared module — frontend (CRA) and api (Workers)
 * aren't set up as a workspace with a shared package in this repo — so
 * they're kept in lockstep by hand; both list exactly
 * `["not_started", "in_progress", "completed"]`.
 *
 * `active` is deliberately absent from GOAL_STATUSES — no UI surface can
 * offer it as something to write anymore. Rows written before this change
 * can still have it stored (this issue's data migration to normalise them
 * is explicitly deferred — see #197), so `normalizeGoalStatus` keeps
 * mapping it to `in_progress` for *display* until that migration runs.
 */
export const GOAL_STATUSES = [
  { value: "not_started", label: "Not Started", color: "#64748B" },
  { value: "in_progress", label: "In Progress", color: "#0B2E59" },
  { value: "completed", label: "Completed", color: "#10B981" },
];

export const GOAL_STATUS_VALUES = GOAL_STATUSES.map((s) => s.value);

const LEGACY_ACTIVE_STATUS = "active";

/** Legacy-tolerant read: maps the pre-#193 `active` value to `in_progress`. */
export function normalizeGoalStatus(status) {
  return status === LEGACY_ACTIVE_STATUS ? "in_progress" : status || "not_started";
}

export function goalStatusMeta(status) {
  const normalized = normalizeGoalStatus(status);
  return GOAL_STATUSES.find((s) => s.value === normalized) || GOAL_STATUSES[0];
}

export function goalStatusColor(status) {
  return goalStatusMeta(status).color;
}

export function goalStatusLabel(status) {
  return goalStatusMeta(status).label;
}

/**
 * The drawn casing of the three statuses ("Not started", "In progress"), as
 * `CoachGoals.dc.html` and `PlanMilestonesScreen` print them (#1219). The
 * values are `GOAL_STATUSES`' own.
 */
export const GOAL_STATUS_OPTIONS_DRAWN = [
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
];
